<template>
  <div class="i-emerging-paths">
    <div class="header">
      <div><strong>Top emerging career paths</strong> <br v-if="isSmOrLarger" />we’ve uncovered for you.</div>
      <div class="sub-header">
        We’ve identified +10 idea job paths that fit your <br v-if="isSmOrLarger" />unique set of skills, experience,
        and traits.
      </div>
    </div>
    <div class="careers">
      <div
        v-for="career in topCareers"
        :key="career.title"
        class="career"
      >
        <div class="top">
          <div class="job-function">{{ career.jobFunction }}</div>
          <div class="title">{{ career.title }}</div>
        </div>
        <div class="bottom">
          <div class="badge">
            You are a <strong>{{ career.match }}% match</strong>
          </div>
          <div class="salaries">
            <div class="salary">
              <div class="figure">${{ career.salary.entry }}</div>
              Entry Level
            </div>
            <div class="salary">
              <div class="figure">${{ career.salary.mid }}</div>
              Mid Level
            </div>
            <div class="salary">
              <div class="figure">${{ career.salary.senior }}</div>
              Senior Level
            </div>
          </div>
        </div>
      </div>
    </div>
    <iBaseButton
      class="button"
      v-bind="buttonStyle"
      text="Unlock My Top Job Fits"
      @clicked="$emit('scroll-to-payment')"
    />
  </div>
</template>

<script lang="ts" setup>
import { computed } from "vue"
import { bluePurpleGradient, white } from "@/styles/colors"
import { breakpointSm } from "@/styles/variables"

import { useReportStore } from "@/stores/report"

import useIsScreenSize from "@/composables/useIsScreenSize"

import iBaseButton from "@/components/base/BaseButton.vue"

// EMITS
defineEmits(["scroll-to-payment"])

// STORES
const reportStore = useReportStore()

// COMPOSABLES
const isSmOrLarger = useIsScreenSize(breakpointSm)

// COMPUTED

/**
 * Returns the top three unique careers based on the user's recommended jobs.
 */
const topCareers = computed(() => {
  const allJobs = reportStore.getRecommendedJobs

  // If there are no jobs, return a default set of careers
  if (!allJobs || allJobs.length === 0) {
    return [
      {
        jobFunction: "Design and Creative",
        title: "Information Technologia",
        match: 94,
        salary: { entry: "59,000", mid: "75,000", senior: "100,000" }
      },
      {
        jobFunction: "Education and Training",
        title: "Web Development Mentor",
        match: 92,
        salary: { entry: "62,000", mid: "84,000", senior: "122,000" }
      },
      {
        jobFunction: "Marketing & Communications",
        title: "Sernior Sales Marketer",
        match: 98,
        salary: { entry: "41,000", mid: "69,000", senior: "90,000" }
      }
    ]
  }

  // Filter to get three unique job functions, discarding "Other" and duplicates
  const uniqueJobFunctions = new Set<string>()
  let keptJobs = []
  let discardedJobs = []
  for (const job of allJobs) {
    if (job.jobFunction?.name && !uniqueJobFunctions.has(job.jobFunction.name) && job.jobFunction.name !== "Other") {
      uniqueJobFunctions.add(job.jobFunction.name)
      keptJobs.push(job)
    } else {
      discardedJobs.push(job)
    }

    // Stop if we have 3 unique job functions
    if (uniqueJobFunctions.size >= 3) {
      break
    }
  }

  // If we have less than 3 unique job functions, add discarded jobs until we reach 3
  if (uniqueJobFunctions.size < 3) {
    keptJobs = keptJobs.concat(discardedJobs.slice(0, 3 - uniqueJobFunctions.size))
  }

  // If we have less than 3 unique job functions, just return the filtered jobs
  return keptJobs?.map((job) => ({
    jobFunction: job.jobFunction?.name || "Marketing & Communications",
    title: job.title || "Information Technology",
    match: job.matchScore || 90,
    salary: {
      entry: job.salaryPositions?.entry?.toLocaleString() || "52,000",
      mid: job.salaryPositions?.mid?.toLocaleString() || "75,000",
      senior: job.salaryPositions?.senior?.toLocaleString() || "100,000"
    }
  }))
})

const buttonStyle = computed(() => {
  return {
    backgroundImage: bluePurpleGradient,
    borderRadius: "8px",
    fontWeight: "500",
    textColor: white,
    width: isSmOrLarger.value ? "max-content" : "100%",
    padding: isSmOrLarger.value ? "8px 70px" : "10px 25px",
    size: "medium"
  }
})
</script>

<style scoped lang="scss">
.i-emerging-paths {
  @include flex(column, center, center, $gap: 35px);

  padding: 50px 15px;
  background-image: url("@/assets/confirm/bg-gradient-multi.png");
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center top;

  @media screen and (min-width: $breakpoint-md) {
    padding: 60px 15px;
    gap: 60px;
  }

  .header {
    @include display-md;
    @include flex(column, start, center, $gap: 20px);

    text-align: center;

    @media screen and (min-width: $breakpoint-md) {
      @include display-lg;
    }

    .sub-header {
      @include text-xl;

      font-weight: 700;
      text-align: center;
    }
  }

  .careers {
    @include flex(row, center, stretch, wrap, $gap: 24px);

    width: 100%;

    .career {
      @include flex(column, space-between, stretch, $gap: 28px);
      @include border-radius-md;
      @include text-sm;

      background-color: $white;
      padding: 24px;
      width: 100%;
      max-width: 420px;
      box-shadow: 0 14px 18px 0 #122b6905;

      .top {
        @include flex(column, start, start, $gap: 8px);

        font-weight: 600;

        .job-function {
          background-image: $blue-purple-gradient;
          color: transparent;
          background-clip: text;
        }

        .title {
          @include text-xl;

          user-select: none;
          filter: blur(5px);
        }
      }

      .bottom {
        @include flex(column, start, stretch, $gap: 15px);

        .badge {
          @include border-radius-xxl;

          width: fit-content;
          background-image: $blue-purple-gradient;
          padding: 3px 12px;
          color: $white;
        }

        .salaries {
          @include flex(row, space-between, stretch, $gap: 5px);

          .salary {
            @include flex(column, center, stretch);

            color: $comet400;

            .figure {
              @include text-md;

              font-weight: 600;
              color: $black;
            }
          }
        }
      }
    }
  }
}
</style>
